import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IGroupOverview } from 'core/hooks';
import {
  useStaffTasksListQuery,
  useAddTaskMutation,
  useChangeTaskStatusMutation,
  useEditTaskMutation,
  useRemoveTaskMutation,
  StaffTasksListDocument,
} from 'core/graphql';

import {
  IStaffTask,
  bucketTasks,
  isClosed,
  localToday,
  DONE,
  OPEN,
} from './taskBuckets';
import { TaskColumns } from './TaskColumns';
import { TaskCreateForm, ICreateTaskDraft } from './TaskCreateForm';
import { TaskDetailsDrawer } from './TaskDetailsDrawer';
import * as S from './TaskBoard.styled';

interface ITaskBoardProps {
  /** Scope the feed (and create form) to one group. */
  groupId?: string;
  /** Scope the feed (and create form) to one student. */
  studentId?: string;
  /** Initial state of the "mine / all" scope toggle. */
  assigneeMe?: boolean;
  /** Group list for the dashboard's create picker. */
  groups?: IGroupOverview[];
  /** Render the inline create form. */
  showCreateForm?: boolean;
  /**
   * Dashboard mode: text search comes from the page's global filter bar.
   * When defined the board hides its own search input.
   */
  externalSearch?: string;
  /** Dashboard mode: group filter from the page's global filter bar. */
  externalGroupId?: string;
}

/**
 * Shared staff-task column board (spec 0003 command-center rework). One flat
 * `staffTasks` query bucketed client-side into five always-visible columns —
 * overdue / today / tomorrow / this week / later(+no-date) — plus a
 * collapsible closed strip. Cards open a right-side details drawer; the round
 * «✓» on a card is the quick done/reopen action. Reused by the dashboard
 * Tasks tab (external filters), the group Tasks tab (`groupId`) and the
 * student Tasks tab (`studentId`).
 */
export const TaskBoard = memo(
  ({
    groupId,
    studentId,
    assigneeMe = false,
    groups,
    showCreateForm = false,
    externalSearch,
    externalGroupId,
  }: ITaskBoardProps) => {
    const { t } = useTranslation();

    const [mine, setMine] = useState(assigneeMe);
    const [localSearch, setLocalSearch] = useState('');
    const [includeClosed, setIncludeClosed] = useState(false);
    const [selectedTask, setSelectedTask] = useState<IStaffTask | null>(null);
    const [drawerVisible, setDrawerVisible] = useState(false);

    const isExternallyFiltered = externalSearch !== undefined;
    const search = (externalSearch !== undefined
      ? externalSearch
      : localSearch
    ).trim();

    const queryVars = {
      groupId: groupId || externalGroupId || undefined,
      studentId: studentId || undefined,
      assigneeMe: mine,
      search: search || undefined,
      includeClosed,
    };
    const { data, loading } = useStaffTasksListQuery({
      variables: queryVars,
      fetchPolicy: 'cache-and-network',
    });
    const tasks = useMemo(() => (data?.staffTasks || []) as IStaffTask[], [
      data,
    ]);

    const refetchQueries = [
      { query: StaffTasksListDocument, variables: queryVars },
    ];
    const [addTask, { loading: adding }] = useAddTaskMutation({
      refetchQueries,
      awaitRefetchQueries: true,
    });
    const [changeStatus, { loading: statusing }] = useChangeTaskStatusMutation({
      refetchQueries,
      awaitRefetchQueries: true,
    });
    const [editTask, { loading: editing }] = useEditTaskMutation({
      refetchQueries,
      awaitRefetchQueries: true,
    });
    const [removeTask, { loading: removing }] = useRemoveTaskMutation({
      refetchQueries,
      awaitRefetchQueries: true,
    });
    const mutating = adding || statusing || editing || removing;

    // Keep the drawer showing fresh data after refetches; if the task left
    // the current selection (e.g. marked done with closed tasks hidden) keep
    // the last snapshot — never close the drawer under the user.
    useEffect(() => {
      if (!selectedTask) return;
      const fresh = tasks.find((task) => task.id === selectedTask.id);
      if (fresh && fresh !== selectedTask) setSelectedTask(fresh);
    }, [tasks, selectedTask]);

    const onCreate = useCallback(
      async (draft: ICreateTaskDraft): Promise<boolean> => {
        const res = await addTask({ variables: { input: draft } });
        return Boolean(res.data?.createStaffTask?.success);
      },
      [addTask],
    );

    const onToggleDone = useCallback(
      (task: IStaffTask) => {
        changeStatus({
          variables: { id: task.id, status: isClosed(task) ? OPEN : DONE },
        });
      },
      [changeStatus],
    );

    const onReschedule = useCallback(
      (taskId: string, dueDate: string) => {
        editTask({ variables: { input: { id: taskId, dueDate } } });
      },
      [editTask],
    );

    const onSaveDescription = useCallback(
      async (taskId: string, description: string): Promise<boolean> => {
        const res = await editTask({
          variables: { input: { id: taskId, description } },
        });
        return Boolean(res.data?.updateStaffTask?.success);
      },
      [editTask],
    );

    const onDelete = useCallback(
      async (taskId: string) => {
        await removeTask({ variables: { id: taskId } });
        setDrawerVisible(false);
      },
      [removeTask],
    );

    const onOpen = useCallback((task: IStaffTask) => {
      setSelectedTask(task);
      setDrawerVisible(true);
    }, []);

    const buckets = useMemo(() => bucketTasks(tasks, localToday()), [tasks]);

    return (
      <S.Card>
        {showCreateForm ? (
          <TaskCreateForm
            groupId={groupId}
            studentId={studentId}
            groups={groups}
            adding={adding}
            onCreate={onCreate}
          />
        ) : null}

        <S.TaskBar>
          <S.Segment>
            <S.SegmentBtn
              type="button"
              active={mine}
              onClick={() => setMine(true)}
            >
              {t('pages.dashboard.scopeMine')}
            </S.SegmentBtn>
            <S.SegmentBtn
              type="button"
              active={!mine}
              onClick={() => setMine(false)}
            >
              {t('pages.dashboard.scopeAll')}
            </S.SegmentBtn>
          </S.Segment>
          {!isExternallyFiltered ? (
            <S.SearchInput
              type="search"
              value={localSearch}
              placeholder={t('pages.tasks.search')}
              onChange={(e) => setLocalSearch(e.target.value)}
            />
          ) : null}
          <S.Toggle>
            <input
              type="checkbox"
              checked={includeClosed}
              onChange={(e) => setIncludeClosed(e.target.checked)}
            />
            {t('pages.tasks.showClosed')}
          </S.Toggle>
        </S.TaskBar>

        {loading && tasks.length === 0 ? (
          <S.Muted>{t('pages.tasks.loading')}</S.Muted>
        ) : (
          <TaskColumns
            buckets={buckets}
            onOpen={onOpen}
            onToggleDone={onToggleDone}
          />
        )}

        <TaskDetailsDrawer
          task={selectedTask}
          visible={drawerVisible}
          onClose={() => setDrawerVisible(false)}
          onToggleDone={onToggleDone}
          onReschedule={onReschedule}
          onSaveDescription={onSaveDescription}
          onDelete={onDelete}
          mutating={mutating}
        />
      </S.Card>
    );
  },
);

TaskBoard.displayName = 'TaskBoard';
